
import { Stethoscope, BarChart3, HardHat, Car, Truck, Shield, Heart, Landmark, Zap, Mountain, Radio, Trophy, Plane, Factory, Leaf } from 'lucide-react';
import type { Product } from './types';

export const industryProducts: Product[] = [
  {
    id: 'healthcare-practice-management',
    title: 'Healthcare Practice Management System',
    subtitle: 'Complete medical practice solution',
    description: 'Integrated healthcare management with patient records, appointment scheduling, billing, and clinical workflow optimization.',
    icon: Stethoscope,
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#healthcare', '#medical-practice', '#patient-management', '#clinical', '#billing'],
    features: [
      'Electronic Health Records (EHR)',
      'Patient Appointment Scheduling',
      'Medical Billing & Insurance Claims',
      'Clinical Workflow Management',
      'Prescription Management',
      'Lab Results Integration',
      'Patient Portal & Communication',
      'Compliance & HIPAA Management',
      'Reporting & Analytics',
      'Mobile Access for Providers',
      'Telemedicine Integration',
      'Revenue Cycle Management'
    ],
    pricing: { setup: 24000, monthly: 799 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'financial-services-platform',
    title: 'Financial Services Platform',
    subtitle: 'Complete financial operations',
    description: 'Comprehensive financial services platform with client management, portfolio tracking, compliance monitoring, and regulatory reporting.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#financial-services', '#wealth-management', '#compliance', '#portfolio', '#advisory'],
    features: [
      'Client Relationship Management',
      'Portfolio Management & Tracking',
      'Risk Assessment & Analysis',
      'Compliance Monitoring & Reporting',
      'Regulatory Filing Automation',
      'Investment Research & Analysis',
      'Client Reporting & Communication',
      'Fee Calculation & Billing',
      'Document Management & Storage',
      'Audit Trail & Record Keeping',
      'Integration with Trading Platforms',
      'Performance Analytics & Benchmarking'
    ],
    pricing: { setup: 32000, monthly: 999 },
    color: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'construction-management-system',
    title: 'Construction Management System',
    subtitle: 'End-to-end project delivery',
    description: 'AI-powered construction management platform with project scheduling, cost tracking, safety compliance, and subcontractor coordination.',
    icon: HardHat,
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#construction', '#project-management', '#building', '#civil-engineering', '#contracting'],
    features: [
      'Project Scheduling & Gantt Charts',
      'Cost Estimation & Budget Tracking',
      'Subcontractor Management',
      'Safety Compliance & Incident Reporting',
      'Building Information Modelling (BIM)',
      'Document Control & Versioning',
      'Daily Log & Progress Reporting',
      'Equipment & Resource Tracking',
      'Permit & Inspection Management',
      'Change Order Processing',
      'Client Collaboration Portal',
      'Mobile Site Inspections'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-amber-500 to-orange-600'
  },
  {
    id: 'automotive-dealership-platform',
    title: 'Automotive Dealership Platform',
    subtitle: 'Intelligent dealership operations',
    description: 'Comprehensive automotive dealership management with inventory tracking, CRM, service scheduling, and F&I workflow automation.',
    icon: Car,
    image: 'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#automotive', '#dealership', '#vehicle-sales', '#fleet', '#motor-trade'],
    features: [
      'Vehicle Inventory Management',
      'Customer Relationship Management',
      'Sales Pipeline & Deal Tracking',
      'F&I Workflow Automation',
      'Service Department Scheduling',
      'Parts Inventory & Ordering',
      'Trade-In Valuation Tools',
      'Digital Retailing & Online Sales',
      'Marketing Campaign Management',
      'Manufacturer Compliance Reporting',
      'Fleet Management Integration',
      'Customer Communication Automation'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-red-500 to-rose-600'
  },
  {
    id: 'logistics-freight-management',
    title: 'Logistics & Freight Management',
    subtitle: 'Global supply chain orchestration',
    description: 'End-to-end logistics platform with route optimisation, freight tracking, warehouse management, and customs documentation.',
    icon: Truck,
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#logistics', '#freight', '#supply-chain', '#warehousing', '#distribution'],
    features: [
      'Route Optimisation & Planning',
      'Real-Time Shipment Tracking',
      'Warehouse Management System',
      'Customs & Trade Documentation',
      'Carrier Management & Rate Shopping',
      'Load Planning & Consolidation',
      'Proof of Delivery Management',
      'Returns & Reverse Logistics',
      'Fleet Maintenance Scheduling',
      'Demand Forecasting & Planning',
      'Multi-Modal Transport Coordination',
      'Compliance & Regulatory Reporting'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-blue-500 to-cyan-600'
  },
  {
    id: 'insurance-operations-platform',
    title: 'Insurance Operations Platform',
    subtitle: 'Intelligent underwriting & claims',
    description: 'AI-driven insurance platform with policy administration, claims processing, underwriting automation, and regulatory compliance.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#insurance', '#underwriting', '#claims', '#actuarial', '#risk-management'],
    features: [
      'Policy Administration System',
      'AI-Powered Claims Processing',
      'Underwriting Automation & Scoring',
      'Premium Calculation Engine',
      'Agent & Broker Portal',
      'Regulatory Compliance Management',
      'Fraud Detection & Prevention',
      'Document Extraction & Processing',
      'Customer Self-Service Portal',
      'Reinsurance Management',
      'Actuarial Reporting & Analytics',
      'Integration with Rating Bureaus'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-indigo-500 to-violet-600'
  },
  {
    id: 'non-profit-management-system',
    title: 'Non-Profit Management System',
    subtitle: 'Mission-driven operations',
    description: 'Purpose-built platform for non-profit organisations with donor management, grant tracking, volunteer coordination, and impact reporting.',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#non-profit', '#charity', '#fundraising', '#volunteer', '#social-impact'],
    features: [
      'Donor Management & CRM',
      'Online Fundraising & Campaigns',
      'Grant Application & Tracking',
      'Volunteer Coordination & Scheduling',
      'Event Management & Ticketing',
      'Impact Measurement & Reporting',
      'Financial Stewardship & Accounting',
      'Membership Management',
      'Automated Thank-You & Tax Receipts',
      'Board & Governance Management',
      'Programme Tracking & Outcomes',
      'Multi-Channel Communication'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-pink-500 to-rose-600'
  },
  {
    id: 'government-services-platform',
    title: 'Government Services Platform',
    subtitle: 'Digital citizen services',
    description: 'Secure government platform for citizen services, permit management, public records, and interdepartmental workflow automation.',
    icon: Landmark,
    image: 'https://images.unsplash.com/photo-1523292562811-8fa7962a78c8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#government', '#public-sector', '#citizen-services', '#municipal', '#council'],
    features: [
      'Citizen Self-Service Portal',
      'Permit & Licence Management',
      'Public Records Management',
      'Interdepartmental Workflows',
      'Council Meeting & Agenda Management',
      'Case Management System',
      'Planning & Zoning Applications',
      'Revenue & Tax Collection',
      'FOIA & Public Records Requests',
      'GIS & Mapping Integration',
      'Emergency Management & Alerts',
      'Accessibility & Compliance (WCAG)'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-slate-500 to-gray-600'
  },
  {
    id: 'energy-utilities-management',
    title: 'Energy & Utilities Management',
    subtitle: 'Smart grid & asset operations',
    description: 'Comprehensive energy management platform with smart metering, asset monitoring, outage management, and regulatory compliance.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#energy', '#utilities', '#smart-grid', '#renewable', '#power-generation'],
    features: [
      'Smart Metering & AMI Integration',
      'Asset Performance Management',
      'Outage Detection & Management',
      'Energy Trading & Procurement',
      'Customer Billing & Self-Service',
      'Grid Monitoring & Analytics',
      'Renewable Energy Management',
      'Regulatory Compliance & Reporting',
      'Field Service Management',
      'Demand Response & Load Management',
      'Carbon Footprint Tracking',
      'Safety & Environmental Monitoring'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-yellow-500 to-amber-600'
  },
  {
    id: 'mining-operations-platform',
    title: 'Mining Operations Platform',
    subtitle: 'Intelligent extraction management',
    description: 'Advanced mining operations platform with geological modelling, fleet management, safety systems, and environmental compliance.',
    icon: Mountain,
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#mining', '#extraction', '#quarrying', '#mineral-processing', '#geological'],
    features: [
      'Geological Modelling & Mapping',
      'Mine Planning & Scheduling',
      'Fleet & Equipment Management',
      'Safety Management & Compliance',
      'Environmental Monitoring & Reporting',
      'Production Tracking & Optimisation',
      'Grade Control & Ore Tracking',
      'Drill & Blast Management',
      'Ventilation & Gas Monitoring',
      'Tailings & Waste Management',
      'Supply Chain & Procurement',
      'Fatigue & Risk Management'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-stone-500 to-zinc-600'
  },
  {
    id: 'telecommunications-platform',
    title: 'Telecommunications Platform',
    subtitle: 'Network & service orchestration',
    description: 'Enterprise telecommunications platform with network management, service provisioning, billing, and customer experience optimisation.',
    icon: Radio,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#telecom', '#network-management', '#isp', '#mobile', '#fibre'],
    features: [
      'Network Monitoring & Management',
      'Service Provisioning & Activation',
      'Billing & Revenue Management',
      'Customer Experience Management',
      'Fault Management & Resolution',
      'Capacity Planning & Optimisation',
      'Tower & Infrastructure Management',
      'SLA Monitoring & Compliance',
      'Number Portability Management',
      'Interconnect & Wholesale Billing',
      'Spectrum Management',
      'Self-Service Customer Portal'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-cyan-500 to-blue-600'
  },
  {
    id: 'sports-recreation-management',
    title: 'Sports & Recreation Management',
    subtitle: 'Complete venue & league operations',
    description: 'All-in-one sports management platform with facility booking, membership management, league scheduling, and performance analytics.',
    icon: Trophy,
    image: 'https://images.unsplash.com/photo-1461896836934-bd45ba8fcf9b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#sports', '#recreation', '#fitness', '#leagues', '#venue-management'],
    features: [
      'Facility & Court Booking',
      'Membership & Subscription Management',
      'League & Tournament Scheduling',
      'Athlete Performance Analytics',
      'Team & Coach Management',
      'Event Ticketing & Registration',
      'Equipment & Inventory Tracking',
      'Payment Processing & Point of Sale',
      'Mobile App for Members',
      'Attendance & Access Control',
      'Coaching & Training Programmes',
      'Sponsor & Partner Management'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-emerald-500 to-green-600'
  },
  {
    id: 'aviation-operations-platform',
    title: 'Aviation Operations Platform',
    subtitle: 'Flight & ground operations',
    description: 'Comprehensive aviation management with flight scheduling, crew management, maintenance tracking, and regulatory compliance.',
    icon: Plane,
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#aviation', '#airline', '#airport', '#ground-handling', '#charter'],
    features: [
      'Flight Scheduling & Dispatch',
      'Crew Rostering & Management',
      'Aircraft Maintenance Tracking',
      'Fuel Management & Optimisation',
      'Ground Handling Operations',
      'Passenger Service Management',
      'Safety & Incident Reporting',
      'Regulatory Compliance (CAA/EASA)',
      'Weight & Balance Calculations',
      'Route Planning & Optimisation',
      'Catering & Provisioning Management',
      'Revenue Management & Pricing'
    ],
    pricing: { setup: 50000, monthly: 2300 },
    color: 'from-sky-500 to-blue-600'
  },
  {
    id: 'industrial-iot-platform',
    title: 'Industrial IoT Platform',
    subtitle: 'Connected factory intelligence',
    description: 'Industrial IoT platform for predictive maintenance, asset monitoring, production optimisation, and digital twin deployment.',
    icon: Factory,
    image: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#industrial-iot', '#smart-factory', '#predictive-maintenance', '#automation', '#industry-4-0'],
    features: [
      'IoT Device Management & Connectivity',
      'Predictive Maintenance Analytics',
      'Digital Twin Modelling',
      'Real-Time Production Monitoring',
      'Energy Consumption Optimisation',
      'Quality Control & Defect Detection',
      'OEE & KPI Dashboards',
      'Edge Computing Integration',
      'SCADA & PLC Integration',
      'Alert Management & Escalation',
      'Environmental Condition Monitoring',
      'Supply Chain Visibility'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'environmental-sustainability-platform',
    title: 'Environmental Sustainability Platform',
    subtitle: 'ESG & carbon management',
    description: 'Enterprise sustainability platform with carbon accounting, ESG reporting, supply chain sustainability tracking, and regulatory compliance.',
    icon: Leaf,
    image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#sustainability', '#esg', '#carbon-management', '#environmental', '#green-tech'],
    features: [
      'Carbon Footprint Accounting',
      'ESG Reporting & Frameworks',
      'Supply Chain Sustainability Tracking',
      'Energy Management & Monitoring',
      'Waste Reduction & Circular Economy',
      'Water Usage Monitoring',
      'Biodiversity Impact Assessment',
      'Regulatory Compliance (CSRD/TCFD)',
      'Sustainability Goal Tracking',
      'Stakeholder Engagement Portal',
      'Life Cycle Assessment Tools',
      'Green Procurement Management'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-green-500 to-emerald-600'
  }
];
